<template>
  <div>
    <ok-cancel-modal
      :title="$t('variables.variableSelector')"
      :ok-button="$t('commons.ok')"
      :cancel-button="$t('commons.cancel')"
      v-on:ok="onOk()"
      v-on:cancel="$emit('close')">
      <div
        slot="body">
        <search-input
          class="search-input"
          v-on:input="onChangeFilter"/>
        <div
          v-has-feature="'Taxonomy'"
        >
          <h3>
            <font-awesome-icon
              :icon="icon.tags"/>
            {{ $t('variables.tags') }}
          </h3>
          <treeselect
            v-model="tags"
            @input="selectedTagsChanged"
            :multiple="true"
            :options="getTreeOptions"
            :valueConsistsOf="'LEAF_PRIORITY'"/>
        </div>
        <div
          class="margin-top-xl variable-list">
          <h3
            class="margin-bottom-l">
            <font-awesome-icon
              :icon="icon.variables"/>
            {{ $t('variables.variables') }}
          </h3>
          <radio
            v-for="(variable, index) in getFilteredVariables.slice(0, maxVariablesShown)"
            :key="index"
            v-on:change="onVariableSelected(variable)"
            :checked="isChecked(variable)"
            radio-size="s"
            :id="String(variable.id)"
            :label="variable.label"/>
            <div
              v-if="getFilteredVariables.length > maxVariablesShown"
              class="and-x-more">
              {{ $t('variables.andXMore', {count: variablesToShow() })}}
            </div>
          </div>
        </div>
    </ok-cancel-modal>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import Radio from '@/components/shared/forms/Radio';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTags,
  faChartBar
} from '@fortawesome/pro-regular-svg-icons';
import SearchInput from '@/components/shared/SearchInput';

export default {
  name: 'VariableSelectorModal',
  components: {
    OkCancelModal,
    Radio,
    Treeselect,
    FontAwesomeIcon,
    SearchInput
  },
  props: {
    value: {
      type: Object,
      default: () => {}
    },
    variables: {
      type: Array,
      default: () => []
    },
    tree: {
      type: Array,
      default: () => []
    },
    selectedTags: {
      type: Array,
      default: () => []
    },
    taggedVariables: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      variableFilter: '',
      tags: this.selectedTags,
      selectedVariable: this.value,
      icon: {
        tags: faTags,
        variables: faChartBar
      },
      maxVariablesShown: 30
    };
  },
  computed: {
    getTreeOptions () {
      return createOptions(this.tree);
    },
    getFilteredVariables () {
      if (this.variableFilter !== '') {
        return this.taggedVariables.filter(variable => {
          return variable.label.toUpperCase().includes(this.variableFilter.toUpperCase());
        });
      }
      return this.taggedVariables;
    }
  },
  methods: {
    isChecked (variable) {
      return this.selectedVariable !== undefined &&
        this.selectedVariable !== null &&
        this.selectedVariable.id === variable.id ? 'checked' : '';
    },
    onVariableSelected (variable) {
      this.selectedVariable = variable;
    },
    onOk () {
      this.$emit('input', this.selectedVariable);
      this.$emit('close');
    },
    selectedTagsChanged (tags) {
      this.$emit('selectedTagsChanged', tags);
    },
    onChangeFilter (filter) {
      this.variableFilter = filter;
    },
    variablesToShow () {
      return this.getFilteredVariables.length - this.maxVariablesShown;
    }
  }
};

const createOptions = (nodes) => {
  const options = [];

  const sortedNodes = nodes.sort((a, b) => {
    return a.text.localeCompare(b.text, undefined, {numeric: true, sensitivity: 'base'});
  });

  for (let i = 0; i < sortedNodes.length; i++) {
    const option = {
      id: sortedNodes[i].data.id,
      label: sortedNodes[i].text
    };

    if (sortedNodes[i].children.length > 0) {
      option.children = createOptions(sortedNodes[i].children);
    }

    options.push(option);
  }

  return options;
};

/*
 * With this property in <treeselect>:
 * value-consists-of="LEAF_PRIORITY"
 * is not neccessary fold logic
 */
const findFoldTags = (ids, nodes, parent = false) => {
  const tags = [];
  for (let i = 0; i < nodes.length; i++) {
    const id = ids.find(id => id === nodes[i].data.id);
    if (id || parent) {
      if (!nodes[i].children || nodes[i].children.length === 0) {
        tags.push({
          id: nodes[i].data.id,
          name: nodes[i].text
        });
      } else {
        const constChildTags = findFoldTags(ids, nodes[i].children, true);
        tags.push(...constChildTags);
      }
    } else if (nodes[i].children && nodes[i].children.length > 0) {
      const constChildTags = findFoldTags(ids, nodes[i].children, false);
      tags.push(...constChildTags);
    }
  }

  return tags;
};

</script>

<style lang="scss" scoped>
  @import "../../stylesheets/variables.scss";

  h3 {
    color: $jamaicol;
  }

  .variable-list {
    min-height: calc(100vh / 2);
    max-height: calc(100vh / 2);
    overflow-y: auto;
  }

  .search-input {
    margin-bottom: $sp-margin;
  }

  .and-x-more {
    color: $jamaicol;
  }

</style>



// WEBPACK FOOTER //
// src/components/variable/VariableSelectorModal.vue